// The 40 supported UI languages - chosen to cover the large majority of the
// world's internet users/population with genuinely maintained translations,
// rather than attempting all ~7,000 living languages (most of which have no
// established digital/UI convention to translate into). `nameNative` is
// shown in the language switcher IN THAT LANGUAGE (so a visitor can always
// find their own language by eye, even if the current UI language is one
// they don't read) - `nameEn` is only used in code comments/docs.
export interface LanguageInfo {
  code: string;
  nameNative: string;
  nameEn: string;
  /** Right-to-left script - flips document direction, see LanguageContext. */
  rtl?: boolean;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: "en", nameNative: "English", nameEn: "English" },
  { code: "es", nameNative: "Español", nameEn: "Spanish" },
  { code: "pt", nameNative: "Português", nameEn: "Portuguese" },
  { code: "fr", nameNative: "Français", nameEn: "French" },
  { code: "de", nameNative: "Deutsch", nameEn: "German" },
  { code: "it", nameNative: "Italiano", nameEn: "Italian" },
  { code: "ru", nameNative: "Русский", nameEn: "Russian" },
  { code: "zh", nameNative: "中文（简体）", nameEn: "Chinese (Simplified)" },
  { code: "ja", nameNative: "日本語", nameEn: "Japanese" },
  { code: "ko", nameNative: "한국어", nameEn: "Korean" },
  { code: "hi", nameNative: "हिन्दी", nameEn: "Hindi" },
  { code: "ar", nameNative: "العربية", nameEn: "Arabic", rtl: true },
  { code: "bn", nameNative: "বাংলা", nameEn: "Bengali" },
  { code: "ur", nameNative: "اردو", nameEn: "Urdu", rtl: true },
  { code: "id", nameNative: "Bahasa Indonesia", nameEn: "Indonesian" },
  { code: "ms", nameNative: "Bahasa Melayu", nameEn: "Malay" },
  { code: "vi", nameNative: "Tiếng Việt", nameEn: "Vietnamese" },
  { code: "th", nameNative: "ไทย", nameEn: "Thai" },
  { code: "tr", nameNative: "Türkçe", nameEn: "Turkish" },
  { code: "fa", nameNative: "فارسی", nameEn: "Persian", rtl: true },
  { code: "pl", nameNative: "Polski", nameEn: "Polish" },
  { code: "uk", nameNative: "Українська", nameEn: "Ukrainian" },
  { code: "nl", nameNative: "Nederlands", nameEn: "Dutch" },
  { code: "sv", nameNative: "Svenska", nameEn: "Swedish" },
  { code: "no", nameNative: "Norsk", nameEn: "Norwegian" },
  { code: "da", nameNative: "Dansk", nameEn: "Danish" },
  { code: "fi", nameNative: "Suomi", nameEn: "Finnish" },
  { code: "el", nameNative: "Ελληνικά", nameEn: "Greek" },
  { code: "cs", nameNative: "Čeština", nameEn: "Czech" },
  { code: "sk", nameNative: "Slovenčina", nameEn: "Slovak" },
  { code: "hu", nameNative: "Magyar", nameEn: "Hungarian" },
  { code: "ro", nameNative: "Română", nameEn: "Romanian" },
  { code: "bg", nameNative: "Български", nameEn: "Bulgarian" },
  { code: "hr", nameNative: "Hrvatski", nameEn: "Croatian" },
  { code: "sr", nameNative: "Српски", nameEn: "Serbian" },
  { code: "sl", nameNative: "Slovenščina", nameEn: "Slovenian" },
  { code: "he", nameNative: "עברית", nameEn: "Hebrew", rtl: true },
  { code: "sw", nameNative: "Kiswahili", nameEn: "Swahili" },
  { code: "tl", nameNative: "Filipino", nameEn: "Filipino" },
  { code: "ta", nameNative: "தமிழ்", nameEn: "Tamil" },
];

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: LanguageCode = "en";

const SUPPORTED_CODE_SET = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));

export function isSupportedLanguage(code: string): code is LanguageCode {
  return SUPPORTED_CODE_SET.has(code);
}

export function isRtl(code: string): boolean {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.rtl === true;
}
